import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import ProductChip from "../automations/ProductChip";
import TriggerSummary from "../automations/TriggerSummary";

const id = "modal-turn-on-automation";
const REASONS = [
  "no_email",
  "invalid_format",
  "bounced",
  "spam",
  "disposable",
  "unsubscribed",
];

const Stat = ({ value, label }) => (
  <div className="ea-stat">
    <strong>{value}</strong>
    <span>{label}</span>
  </div>
);

Stat.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

const SkippedBox = ({ skipped, tr }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="ea-skipped">
      <header>
        <strong>{tr("skipped.title", { count: skipped.total })}</strong>
        <button type="button" onClick={() => setOpen((value) => !value)}>
          {tr(open ? "skipped.hideList" : "skipped.viewList")}
        </button>
      </header>
      <ul>
        {REASONS.filter((reason) => skipped.reasons[reason]).map((reason) => (
          <li key={reason}>
            <span>{tr(`skipped.reasons.${reason}`)}</span>
            <strong>{skipped.reasons[reason]}</strong>
          </li>
        ))}
      </ul>
      {open ? (
        <ul className="ea-skipped__list">
          {skipped.list.map((person) => (
            <li key={`${person.email}-${person.name}`}>
              <span>{person.name || person.email || "—"}</span>
              <span>{tr(`skipped.reasons.${person.reason}`)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <p>{tr("skipped.note")}</p>
    </div>
  );
};

SkippedBox.propTypes = {
  skipped: PropTypes.shape({
    total: PropTypes.number.isRequired,
    reasons: PropTypes.object.isRequired,
    list: PropTypes.array.isRequired,
  }).isRequired,
  tr: PropTypes.func.isRequired,
};

const hasMarketing = (steps = []) =>
  steps.some((step) => step.type === "email" && step.config?.marketing);

// Turning an automation on: when people already qualify, the owner chooses whether they get it
const TurnOnAutomation = ({
  detail,
  backfill,
  definition,
  options,
  returnTo,
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.turnOn.${key}`, params);
  const { automation, steps } = detail ?? {};
  const waiting = backfill?.supported && backfill.qualify > 0;
  const [mode, setMode] = useState(
    backfill?.willGet > 0 ? "include" : "new_only"
  );
  if (!automation) return null;

  const withName = {
    ...automation,
    trigger: {
      ...automation.trigger,
      name: definition?.name ?? automation.trigger.key,
    },
  };
  const sendTo = mode === "include" ? (backfill?.willGet ?? 0) : 0;

  return (
    <form
      action={`/actions/automations/${automation._id}/status`}
      method="POST"
      data-noreset
    >
      <input type="hidden" name="status" value="active" />
      <input type="hidden" name="returnTo" value={returnTo} />
      {waiting ? <input type="hidden" name="backfill" value={mode} /> : null}
      <Modal
        name={id}
        key={id}
        className="ea-modal ea-modal--turn-on"
        customTitle={
          <h2 className="ea-modal__title">
            {tr("title", { name: automation.name })}
            <ProductChip product={automation.product} />
          </h2>
        }
      >
        <div className="body">
          <p className="ea-modal__lead">
            <TriggerSummary automation={withName} options={options} compact>
              {tr(hasMarketing(steps) ? "marketing" : "transactional")}
            </TriggerSummary>
          </p>
          {waiting ? (
            <>
              <div className="ea-stats">
                <Stat value={backfill.qualify} label={tr("stats.qualify")} />
                <Stat value={backfill.willGet} label={tr("stats.willGet")} />
                <Stat
                  value={backfill.skipped.total}
                  label={tr("stats.skipped")}
                />
              </div>
              <fieldset className="ea-choices">
                <legend className="sr-only">
                  {tr("title", { name: automation.name })}
                </legend>
                {["include", "new_only"].map((choice) => (
                  <label
                    key={choice}
                    className={`ea-choice${mode === choice ? " ea-choice--selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="mode"
                      value={choice}
                      className="sr-only"
                      checked={mode === choice}
                      onChange={() => setMode(choice)}
                    />
                    <i className="ea-choice__radio" aria-hidden="true" />
                    <strong>
                      {choice === "include"
                        ? tr("include.title", { count: backfill.qualify })
                        : tr("newOnly.title")}
                    </strong>
                    <span>
                      {tr(
                        choice === "include" ? "include.body" : "newOnly.body"
                      )}
                    </span>
                  </label>
                ))}
              </fieldset>
              {backfill.skipped.total > 0 ? (
                <SkippedBox skipped={backfill.skipped} tr={tr} />
              ) : null}
              <div className="chip info ea-timing">{tr("timing")}</div>
            </>
          ) : (
            <p className="ea-modal__lead">{tr("nobody")}</p>
          )}
        </div>
        <footer className="foot">
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
          <a
            href="/actions/refresh?modal=&automation="
            data-button-type="secondary"
            data-action="cancel"
            role="button"
          >
            {tr("cancel")}
          </a>
          <button type="submit" data-button-type="primary">
            {waiting && sendTo > 0
              ? tr("confirmSend", { count: sendTo })
              : tr("confirm")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

TurnOnAutomation.propTypes = {
  detail: PropTypes.shape({
    automation: PropTypes.object,
    steps: PropTypes.array,
  }),
  backfill: PropTypes.shape({
    supported: PropTypes.bool,
    qualify: PropTypes.number,
    willGet: PropTypes.number,
    skipped: PropTypes.object,
  }),
  definition: PropTypes.object,
  options: PropTypes.object,
  returnTo: PropTypes.string.isRequired,
};

export default TurnOnAutomation;
